import { find, minBy, startCase } from 'lodash';

import { FastProductDetails, QuoteType } from '@common/types';
import {
  Premium,
  Suggestion,
  TermQuotePlanDetails,
  useTermQuoteStore
} from '@store/termquote';
import { Dayjs, dayjs, DEFAULT_DATE_FORMAT } from '@ui';
import { convertToINR } from '@utils';
import { DATE_FORMAT } from '@utils/constants';

import { TERM_PRODUCTS_SLUGS } from './constants';

export const useGenTermPath = (path: string) => {
  const quote = useTermQuoteStore.getState().quote;
  const product = useTermQuoteStore.getState().plan.product;
  const slug = TERM_PRODUCTS_SLUGS[product];
  if (quote === QuoteType.Normal) {
    return `/term/quote/${path}`;
  } else {
    return `/term/fq/${slug}/${path}`;
  }
};

export const getProductDetailsFromSlug = (
  slug: string
): FastProductDetails | undefined => {
  const product = find(
    Object.keys(TERM_PRODUCTS_SLUGS),
    (o: string) => TERM_PRODUCTS_SLUGS[o] === slug
  );
  return product
    ? {
        productName: product,
        productSlug: product ? TERM_PRODUCTS_SLUGS[product] : ''
      }
    : undefined;
};

/**
 * Get the minimum premium from the suggestions
 * @param suggestion
 * @returns Premium
 */
export const getMinimumPremium = (suggestion: Suggestion | null): Premium => {
  if (!suggestion)
    return {
      basePlan: { base: 0, tax: 0 },
      total: { base: 0, tax: 0 },
      adhb: { base: 0, tax: 0 },
      acb: { base: 0, tax: 0 },
      ci: { base: 0, tax: 0 },
      wop: { base: 0, tax: 0 },
      secondYearPremium: { baseWithTax: 0, tax: 0, base: 0 },
      firstYearDiscount: 0
    };

  const filtered = suggestion.products.filter(
    (product) => product.available && product.success
  );

  const minimum = minBy(filtered, (obj) => obj.basePlan.base + obj.basePlan.tax);
  return {
    basePlan: minimum?.basePlan || { base: 0, tax: 0 },
    total: minimum?.basePlan || { base: 0, tax: 0 },
    adhb: { base: 0, tax: 0 },
    acb: { base: 0, tax: 0 },
    ci: { base: 0, tax: 0 },
    wop: { base: 0, tax: 0 },
    secondYearPremium: { baseWithTax: 0, tax: 0, base: 0 },
    firstYearDiscount: 0
  };
};

export const getPremium = (product?: string, suggestion?: Suggestion) => {
  if (product && suggestion) {
    const productPremium = suggestion.products.find(
      (suggProd) => suggProd.key === product
    );
    if (suggestion && productPremium?.available && productPremium.success) {
      return {
        basePlan: productPremium.basePlan,
        adhb: { base: 0, tax: 0 },
        acb: { base: 0, tax: 0 },
        ci: { base: 0, tax: 0 },
        wop: { base: 0, tax: 0 },
        tillAge: productPremium.tillAge,
        total: productPremium.basePlan,
        secondYearPremium: productPremium.secondYearPremium,
        firstYearDiscount: productPremium.firstYearDiscount
      };
    }
  }
  return {
    basePlan: { base: 0, tax: 0 },
    total: { base: 0, tax: 0 },
    adhb: { base: 0, tax: 0 },
    acb: { base: 0, tax: 0 },
    ci: { base: 0, tax: 0 },
    wop: { base: 0, tax: 0 },
    secondYearPremium: { baseWithTax: 0, tax: 0, base: 0 },
    firstYearDiscount: 0
  };
};

export const getCountOfAvailableTermPlans = (suggestion: Suggestion) => {
  return suggestion && suggestion.products
    ? suggestion.products.filter((product) => product.available && product.success)
        .length
    : 0;
};

export const getUserCoverDetailsString = (
  plan: TermQuotePlanDetails,
  premium?: Premium
) => {
  const coverInr = plan && plan.cover ? convertToINR(plan.cover, 2, true) : '';
  return `${coverInr} till ${
    premium &&
    premium.tillAge &&
    premium.tillAge < plan?.tillAge &&
    !plan.productVariant
      ? premium.tillAge
      : plan?.tillAge
  } years old`;
};

export const getUserBasicDetailsString = (plan: TermQuotePlanDetails) => {
  const format = dayjs(plan.dob, DEFAULT_DATE_FORMAT, true).isValid()
    ? DEFAULT_DATE_FORMAT
    : DATE_FORMAT;
  return `${startCase(plan?.gender)}, ${dayjs().diff(
    dayjs(plan?.dob, format),
    'year'
  )} Years, ${startCase(plan?.tobacco ? 'Smoker' : 'Non-Smoker')}${
    plan?.is_nri || plan?.isNRI ? ', NRI' : ''
  } `;
};

export const getAgeFromDob = (dob: Dayjs): number => {
  const format = dayjs(dob, DEFAULT_DATE_FORMAT, true).isValid()
    ? DEFAULT_DATE_FORMAT
    : DATE_FORMAT;
  return dayjs().diff(dayjs(dob, format), 'year');
};
